import { HttpHeaders, HttpParamsOptions } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EInterceptorOptionsHTTPHeaders } from "../interceptors/EInterceptorOptionsHTTPHeaders";
import moment from "moment";

@Injectable()
export class Utilities {
  public static readonly captionAndMessageSeparator = ":";
  public static readonly noNetworkMessageCaption = "No Network";
  public static readonly noNetworkMessageDetail =
    "The server cannot be reached";
  public static readonly accessDeniedMessageCaption = "Access Denied!";
  public static readonly accessDeniedMessageDetail = "";

  public static ReplaceAll = function (str, search, replace) {
    if (replace === undefined) {
      return str.toString();
    }
    return str.split(search).join(replace);
  };

  public static JSonTryParse(value: string) {
    try {
      return JSON.parse(value);
    } catch (e) {
      if (value === "undefined") {
        return void 0;
      }

      return value;
    }
  }

  public static splitInTwo(
    text: string,
    separator: string
  ): { firstPart: string; secondPart: string } {
    const separatorIndex = text.indexOf(separator);

    if (separatorIndex == -1) {
      return { firstPart: text, secondPart: null };
    }

    const part1 = text.substr(0, separatorIndex).trim();
    const part2 = text.substr(separatorIndex + 1).trim();

    return { firstPart: part1, secondPart: part2 };
  }

  public static safeStringify(object) {
    let result: string;

    try {
      result = JSON.stringify(object);
      return result;
    } catch (error) {}

    const simpleObject = {};

    for (const prop in object) {
      if (!object.hasOwnProperty(prop)) {
        continue;
      }
      if (typeof object[prop] == "object") {
        continue;
      }
      if (typeof object[prop] == "function") {
        continue;
      }
      simpleObject[prop] = object[prop];
    }

    result = "[***Sanitized Object***]: " + JSON.stringify(simpleObject);

    return result;
  }

  public static getQueryParamsFromString(paramString: string) {
    if (!paramString) {
      return null;
    }

    const params: { [key: string]: string } = {};

    for (const param of paramString.split("&")) {
      const keyValue = Utilities.splitInTwo(param, "=");
      params[keyValue.firstPart] = keyValue.secondPart;
    }

    return params;
  }

  public static getHttpResponseMessage(data: Response | any): string[] {
    const responses: string[] = [];

    if (data instanceof Response) {
      if (this.checkNoNetwork(data)) {
        responses.push(
          `${this.noNetworkMessageCaption}${this.captionAndMessageSeparator} ${this.noNetworkMessageDetail}`
        );
      } else {
        try {
          const responseObject = data.json();

          if (
            typeof responseObject === "object" ||
            (responseObject as any) instanceof Object
          ) {
            for (const key in responseObject) {
              if (key) {
                responses.push(
                  `${key}${this.captionAndMessageSeparator} ${responseObject[key]}`
                );
              } else if (responseObject[key]) {
                responses.push(responseObject[key].toString());
              }
            }
          }
        } catch (error) {}
      }

      if (!responses.length && data.text()) {
        responses.push(`${data.statusText}: ${data.text()}`);
      }
    }

    if (!responses.length) {
      responses.push(data.toString());
    }

    if (this.checkAccessDenied(data)) {
      responses.splice(
        0,
        0,
        `${this.accessDeniedMessageCaption}${this.captionAndMessageSeparator} ${this.accessDeniedMessageDetail}`
      );
    }

    return responses;
  }

  public static checkNoNetwork(response: Response) {
    if (response instanceof Response) {
      return response.status == 0;
    }

    return false;
  }

  public static checkAccessDenied(response: Response) {
    if (response instanceof Response) {
      return response.status == 403;
    }

    return false;
  }

  public static checkNotFound(response: Response) {
    if (response instanceof Response) {
      return response.status == 404;
    }

    return false;
  }

  public static checkIsLocalHost(url: string, base?: string) {
    if (url) {
      const location = new URL(url, base);
      return (
        location.hostname === "localhost" || location.hostname === "127.0.0.1"
      );
    }

    return false;
  }

  public static findHttpResponseMessage(
    messageToFind: string,
    data: Response | any,
    seachInCaptionOnly = true,
    includeCaptionInResult = false
  ): string {
    const searchString = messageToFind.toLowerCase();
    const httpMessages = this.getHttpResponseMessage(data);

    for (const message of httpMessages) {
      const fullMessage = Utilities.splitInTwo(
        message,
        this.captionAndMessageSeparator
      );

      if (
        fullMessage.firstPart &&
        fullMessage.firstPart.toLowerCase().indexOf(searchString) != -1
      ) {
        return includeCaptionInResult
          ? message
          : fullMessage.secondPart || fullMessage.firstPart;
      }
    }

    if (!seachInCaptionOnly) {
      for (const message of httpMessages) {
        if (message.toLowerCase().indexOf(searchString) != -1) {
          if (includeCaptionInResult) {
            return message;
          } else {
            const fullMessage = Utilities.splitInTwo(
              message,
              this.captionAndMessageSeparator
            );
            return fullMessage.secondPart || fullMessage.firstPart;
          }
        }
      }
    }

    return null;
  }

  public static showNotificationHeader(show: boolean) {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Show-Saved-Notification": show.toString(),
    });
    const options = { headers };
    return options;
  }

  /**@description use this method to change interceptor logic. Appends headers to do so. @global EInterceptorOptionsHTTPHeaders contains all header options with descriptions */
  public static appendHTTPHeaderForInterceptor(
    headerPropNameToAdd: EInterceptorOptionsHTTPHeaders[]
  ): HttpHeaders {
    var headersOut: HttpHeaders = new HttpHeaders();

    headerPropNameToAdd.forEach((headerProp) => {
      headersOut = headersOut.append(headerProp, "true");
    });
    return headersOut;
  }

  public static compareString(a, b) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }

  public static empty(mixedVar: any): boolean {
    let undef;
    let key;
    let i;
    let len;
    const emptyValues = [undef, null, false, 0, "", "0"];
    for (i = 0, len = emptyValues.length; i < len; i++) {
        if (mixedVar === emptyValues[i]) {
            return true;
        }
    }
    if (typeof mixedVar === "object") {
        for (key in mixedVar) {
            if (mixedVar.hasOwnProperty(key)) {
                return false;
            }
        }
        return true;
    }
        return false;
  }

  public static areObjectsEqual(obj1: any, obj2: any): boolean {
    // Check if both objects are null or undefined
    if (obj1 == null && obj2 == null) {
        return true;
    }

    // Check if only one object is null or undefined
    if (obj1 == null || obj2 == null) {
        return false;
    }

    // Check if the number of properties is different
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
        return false;
    }

    // Iterate over the properties of obj1 and compare with obj2
    for (const key in obj1) {
        if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
            // Check for deep equality if both values are objects
            if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
                if (!this.areObjectsEqual(obj1[key], obj2[key])) {
                    return false;
                }
            } else {
                // Compare primitive values
                if (obj1[key] !== obj2[key]) {
                    return false;
                }
            }
        } else {
            return false; // One object has a property that the other doesn't
        }
    }

    return true;
  }

  public static truncateDecimalPlaces(value: number | null | undefined, decimalPlaces = 2): number {
    if(value == null || !Number.isFinite(value)) {
      return value;
    }

    const [coefficient, exponent = "0"] = value.toString().split("e");
    const shiftedValue = Number(`${coefficient}e${Number(exponent) + decimalPlaces}`);
    const truncatedValue = Math.trunc(shiftedValue);

    return Number(`${truncatedValue}e-${decimalPlaces}`);
  }
}
